import { useState, useEffect } from 'react';
import { client } from '../../sdkAdapter';
import Swal from 'sweetalert2';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import Button from '../ui/button/Button';
import MediaPickerModal from '../common/MediaPickerModal';

interface ThemeSettingsModalProps {
    theme: any;
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
}

export default function ThemeSettingsModal({ theme, isOpen, onClose, onSave }: ThemeSettingsModalProps) {
    // General Settings
    const [logoFileId, setLogoFileId] = useState<number | undefined>(undefined);
    const [logoSize, setLogoSize] = useState('medium');

    // Preloader Settings
    const [preloaderEnabled, setPreloaderEnabled] = useState(false);
    const [preloaderBackground, setPreloaderBackground] = useState('#ffffff');
    const [preloaderFileId, setPreloaderFileId] = useState<number | undefined>(undefined);

    // Typography & Colors
    const [primaryColor, setPrimaryColor] = useState('#000000');
    const [secondaryColor, setSecondaryColor] = useState('#ffffff');
    const [bodyFontFamily, setBodyFontFamily] = useState('Inter');
    const [bodyFontSize, setBodyFontSize] = useState('16px');
    const [bodyFontColor, setBodyFontColor] = useState('#000000');
    const [bodyLineHeight, setBodyLineHeight] = useState(1.5);

    const [headingFontFamily, setHeadingFontFamily] = useState('Inter');
    const [headingFontSize, setHeadingFontSize] = useState('32px');
    const [headingFontColor, setHeadingFontColor] = useState('#000000');
    const [headingLineHeight, setHeadingLineHeight] = useState(1.2);

    // Hidden Settings (Preserved)
    const [bodyFontStyle, setBodyFontStyle] = useState('normal');
    const [bodyLetterSpacing, setBodyLetterSpacing] = useState('normal');
    const [headingFontStyle, setHeadingFontStyle] = useState('normal');
    const [headingLetterSpacing, setHeadingLetterSpacing] = useState('normal');

    // Custom Code
    const [customCss, setCustomCss] = useState('');
    const [customJs, setCustomJs] = useState('');

    const [isSaving, setIsSaving] = useState(false);
    const [activePicker, setActivePicker] = useState<'logo' | 'preloader' | null>(null);

    useEffect(() => {
        if (theme?.settings) {
            const s = theme.settings;
            // General
            setLogoFileId(s.logo?.file_id);
            setLogoSize(s.logo?.size || 'medium');

            // Preloader
            setPreloaderEnabled(s.preloader?.enabled ?? false);
            setPreloaderBackground(s.preloader?.background || '#ffffff');
            setPreloaderFileId(s.preloader?.file_id);

            // Typography
            setPrimaryColor(s.typography?.primary_color || '#000000');
            setSecondaryColor(s.typography?.secondary_color || '#ffffff');

            setBodyFontFamily(s.typography?.body_font?.font_family || 'Inter');
            setBodyFontSize(s.typography?.body_font?.font_size || '16px');
            setBodyFontColor(s.typography?.body_font?.font_color || '#000000');
            setBodyLineHeight(s.typography?.body_font?.line_height || 1.5);
            setBodyFontStyle(s.typography?.body_font?.font_style || 'normal');
            setBodyLetterSpacing(s.typography?.body_font?.letter_spacing || 'normal');

            setHeadingFontFamily(s.typography?.heading_font?.font_family || 'Inter');
            setHeadingFontSize(s.typography?.heading_font?.font_size || '32px');
            setHeadingFontColor(s.typography?.heading_font?.font_color || '#000000');
            setHeadingLineHeight(s.typography?.heading_font?.line_height || 1.2);
            setHeadingFontStyle(s.typography?.heading_font?.font_style || 'normal');
            setHeadingLetterSpacing(s.typography?.heading_font?.letter_spacing || 'normal');

            // Custom Code
            setCustomCss(s.custom_code?.custom_css || '');
            setCustomJs(s.custom_code?.custom_js || '');
        }
    }, [theme]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const payload = {
                logo: {
                    file_id: logoFileId,
                    size: logoSize
                },
                preloader: {
                    enabled: preloaderEnabled,
                    background: preloaderBackground,
                    file_id: preloaderFileId
                },
                typography: {
                    primary_color: primaryColor,
                    secondary_color: secondaryColor,
                    body_font: {
                        font_family: bodyFontFamily,
                        font_size: bodyFontSize,
                        font_color: bodyFontColor,
                        line_height: bodyLineHeight,
                        font_style: bodyFontStyle,
                        letter_spacing: bodyLetterSpacing
                    },
                    heading_font: {
                        font_family: headingFontFamily,
                        font_size: headingFontSize,
                        font_color: headingFontColor,
                        line_height: headingLineHeight,
                        font_style: headingFontStyle,
                        letter_spacing: headingLetterSpacing
                    }
                },
                custom_code: {
                    custom_css: customCss,
                    custom_js: customJs
                }
            };

            await client.postApiThemeSettingsUpdateId({
                id: theme.id.toString(),
                postApiThemeSettingsUpdateIdRequest: payload as any
            });
            onSave();
            onClose();
        } catch (err) {
            console.error('Failed to update theme settings:', err);
            Swal.fire({
                title: 'Error',
                text: 'Failed to save settings',
                icon: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
            <div
                className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                    <h3 className="text-xl font-semibold text-gray-800 dark:text-white">Theme Settings: {theme?.name}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    <Tabs defaultValue="general">
                        <TabsList className="mb-6">
                            <TabsTrigger value="general">General</TabsTrigger>
                            <TabsTrigger value="typography">Typography & Colors</TabsTrigger>
                            <TabsTrigger value="preloader">Preloader</TabsTrigger>
                            <TabsTrigger value="custom">Custom Code</TabsTrigger>
                        </TabsList>

                        <TabsContent value="general" className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Logo</label>
                                <div className="flex items-center gap-4">
                                    <div className="w-32 h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center overflow-hidden bg-gray-50 dark:bg-gray-900 text-center">
                                        {logoFileId ? (
                                            <div className="p-2">
                                                <span className="text-xs text-gray-500 block">Selected File ID:</span>
                                                <span className="text-lg font-bold text-gray-800 dark:text-white">{logoFileId}</span>
                                            </div>
                                        ) : (
                                            <span className="text-xs text-gray-400">No logo selected</span>
                                        )}
                                    </div>
                                    <Button variant="outline" size="sm" onClick={() => setActivePicker('logo')}>
                                        Select from Media
                                    </Button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Logo Size</label>
                                <select
                                    value={logoSize}
                                    onChange={(e) => setLogoSize(e.target.value)}
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                                >
                                    <option value="small">Small</option>
                                    <option value="medium">Medium</option>
                                    <option value="large">Large</option>
                                </select>
                            </div>
                        </TabsContent>

                        <TabsContent value="typography" className="space-y-6">
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Primary Color</label>
                                    <div className="flex gap-2">
                                        <div className="relative w-12 h-12 rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden shrink-0">
                                            <input
                                                type="color"
                                                value={primaryColor}
                                                onChange={(e) => setPrimaryColor(e.target.value)}
                                                className="absolute inset-0 w-full h-full scale-150 cursor-pointer"
                                            />
                                        </div>
                                        <input
                                            type="text"
                                            value={primaryColor}
                                            onChange={(e) => setPrimaryColor(e.target.value)}
                                            className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white uppercase"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Secondary Color</label>
                                    <div className="flex gap-2">
                                        <div className="relative w-12 h-12 rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden shrink-0">
                                            <input
                                                type="color"
                                                value={secondaryColor}
                                                onChange={(e) => setSecondaryColor(e.target.value)}
                                                className="absolute inset-0 w-full h-full scale-150 cursor-pointer"
                                            />
                                        </div>
                                        <input
                                            type="text"
                                            value={secondaryColor}
                                            onChange={(e) => setSecondaryColor(e.target.value)}
                                            className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white uppercase"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-xl space-y-4">
                                <h4 className="font-semibold text-gray-800 dark:text-white text-sm uppercase tracking-wider">Body Font</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="col-span-2 sm:col-span-1">
                                        <label className="block text-xs text-gray-500 mb-1">Font Family</label>
                                        <input type="text" value={bodyFontFamily} onChange={(e) => setBodyFontFamily(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700" />
                                    </div>
                                    <div className="col-span-2 sm:col-span-1">
                                        <label className="block text-xs text-gray-500 mb-1">Font Size</label>
                                        <input type="text" value={bodyFontSize} onChange={(e) => setBodyFontSize(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700" />
                                    </div>
                                    <div className="col-span-2 sm:col-span-1">
                                        <label className="block text-xs text-gray-500 mb-1">Font Color</label>
                                        <div className="flex gap-2">
                                            <input type="color" value={bodyFontColor} onChange={(e) => setBodyFontColor(e.target.value)} className="w-10 h-10 rounded border border-gray-300 dark:border-gray-600" />
                                            <input type="text" value={bodyFontColor} onChange={(e) => setBodyFontColor(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 uppercase" />
                                        </div>
                                    </div>
                                    <div className="col-span-2 sm:col-span-1">
                                        <label className="block text-xs text-gray-500 mb-1">Line Height</label>
                                        <input type="number" step="0.1" value={bodyLineHeight} onChange={(e) => setBodyLineHeight(parseFloat(e.target.value))} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700" />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-xl space-y-4">
                                <h4 className="font-semibold text-gray-800 dark:text-white text-sm uppercase tracking-wider">Heading Font</h4>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="col-span-2 sm:col-span-1">
                                        <label className="block text-xs text-gray-500 mb-1">Font Family</label>
                                        <input type="text" value={headingFontFamily} onChange={(e) => setHeadingFontFamily(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700" />
                                    </div>
                                    <div className="col-span-2 sm:col-span-1">
                                        <label className="block text-xs text-gray-500 mb-1">Font Size</label>
                                        <input type="text" value={headingFontSize} onChange={(e) => setHeadingFontSize(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700" />
                                    </div>
                                    <div className="col-span-2 sm:col-span-1">
                                        <label className="block text-xs text-gray-500 mb-1">Font Color</label>
                                        <div className="flex gap-2">
                                            <input type="color" value={headingFontColor} onChange={(e) => setHeadingFontColor(e.target.value)} className="w-10 h-10 rounded border border-gray-300 dark:border-gray-600" />
                                            <input type="text" value={headingFontColor} onChange={(e) => setHeadingFontColor(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 uppercase" />
                                        </div>
                                    </div>
                                    <div className="col-span-2 sm:col-span-1">
                                        <label className="block text-xs text-gray-500 mb-1">Line Height</label>
                                        <input type="number" step="0.1" value={headingLineHeight} onChange={(e) => setHeadingLineHeight(parseFloat(e.target.value))} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700" />
                                    </div>
                                </div>
                            </div>
                        </TabsContent>

                        <TabsContent value="preloader" className="space-y-6">
                            <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl">
                                <input
                                    type="checkbox"
                                    id="preloader-enabled"
                                    checked={preloaderEnabled}
                                    onChange={(e) => setPreloaderEnabled(e.target.checked)}
                                    className="h-5 w-5 text-brand-500 focus:ring-brand-500 border-gray-300 rounded cursor-pointer"
                                />
                                <label htmlFor="preloader-enabled" className="text-base font-medium text-gray-700 dark:text-gray-300 cursor-pointer">Enable Preloader</label>
                            </div>

                            <div className={preloaderEnabled ? 'opacity-100' : 'opacity-50 pointer-events-none'}>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Background Color</label>
                                        <div className="flex gap-2">
                                            <input type="color" value={preloaderBackground} onChange={(e) => setPreloaderBackground(e.target.value)} className="h-12 w-12 rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden" />
                                            <input type="text" value={preloaderBackground} onChange={(e) => setPreloaderBackground(e.target.value)} className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 uppercase" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Preloader Image</label>
                                        <div className="flex items-center gap-4">
                                            <div className="w-24 h-24 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center overflow-hidden bg-gray-50 dark:bg-gray-900 text-center">
                                                {preloaderFileId ? (
                                                    <span className="text-[10px] font-bold">ID: {preloaderFileId}</span>
                                                ) : (
                                                    <span className="text-[10px] text-gray-400">No image</span>
                                                )}
                                            </div>
                                            <Button variant="outline" size="sm" onClick={() => setActivePicker('preloader')}>
                                                Select
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </TabsContent>

                        <TabsContent value="custom" className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Custom CSS</label>
                                <textarea
                                    value={customCss}
                                    onChange={(e) => setCustomCss(e.target.value)}
                                    rows={10}
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-900 font-mono text-sm focus:ring-2 focus:ring-brand-500 focus:bg-white dark:focus:bg-gray-800"
                                    placeholder="/* Your custom CSS here */"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Custom JS</label>
                                <textarea
                                    value={customJs}
                                    onChange={(e) => setCustomJs(e.target.value)}
                                    rows={10}
                                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-900 font-mono text-sm focus:ring-2 focus:ring-brand-500 focus:bg-white dark:focus:bg-gray-800"
                                    placeholder="// Your custom JS here"
                                />
                            </div>
                        </TabsContent>
                    </Tabs>
                </div>

                <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 bg-gray-50 dark:bg-gray-900/50">
                    <Button variant="outline" onClick={onClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save Theme Settings'}
                    </Button>
                </div>
            </div>

            <MediaPickerModal
                isOpen={activePicker !== null}
                onClose={() => setActivePicker(null)}
                onSelect={(file) => {
                    if (activePicker === 'logo') {
                        setLogoFileId(file.id);
                    } else if (activePicker === 'preloader') {
                        setPreloaderFileId(file.id);
                    }
                    setActivePicker(null);
                }}
                title={activePicker === 'logo' ? 'Select Logo' : 'Select Preloader Image'}
            />
        </div>
    );
}
